import { createPortal } from 'react-dom';
import { useAnchoredPopup } from '../../hooks/useAnchoredPopup';

export interface MasterPopupSelectOption {
  label: string;
  value: string;
}

interface MasterPopupSelectProps {
  value: string;
  options: readonly MasterPopupSelectOption[];
  onChange: (next: string) => void;
  /** Names the control, since neither use carries a visible label. */
  ariaLabel: string;
  /** Names the list, for the same reason. */
  listLabel: string;
  /** Width modifier on the trigger -- the reference sizes each use its own. */
  className?: string;
  /** Panel width in px. */
  panelWidth?: number;
  /** Shown when `value` matches no option. */
  fallbackLabel?: string;
}

/* The reference's own numbers for this popup. */
const DEFAULT_PANEL_WIDTH = 190;
const OPTION_HEIGHT = 40;
const PANEL_PADDING = 14;

/** Below the record dialogs, above the table -- the reference's own level. */
const PANEL_Z_INDEX = 45;

/**
 * The reference's select-shaped button and its ticked list.
 *
 * Two controls are drawn this way and they differ only in width: the toolbar's
 * status filter at 150px and the paginator's page size at 104px. Both are a
 * 48px box at 10px radius over #E5E7EE holding a value and a chevron that turns
 * over while the list is open, above a card of 40px rows where the applied one
 * is tinted and ticked.
 *
 * They share `useAnchoredPopup` with the row action menu, so all three are
 * placed from their trigger's measured box and dismiss on the same three things.
 */
export function MasterPopupSelect({
  value,
  options,
  onChange,
  ariaLabel,
  listLabel,
  className = '',
  panelWidth = DEFAULT_PANEL_WIDTH,
  fallbackLabel,
}: MasterPopupSelectProps) {
  const { open, style, popupRef, toggle, close } = useAnchoredPopup({
    width: panelWidth,
    height: options.length * OPTION_HEIGHT + PANEL_PADDING,
    /* Right-aligned: both uses sit at the right-hand end of their row. */
    align: 'end',
    zIndex: PANEL_Z_INDEX,
  });

  /* An unrecognised value reads as the fallback rather than an empty control. */
  const label =
    options.find((option) => option.value === value)?.label ??
    fallbackLabel ??
    options[0]?.label ??
    '';

  return (
    <>
      <button
        type="button"
        className={`ms__select ${className}`.trim()}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={toggle}
      >
        <span className="ms__select-label">{label}</span>
        <i
          className={`pi pi-chevron-down ms__select-chevron${open ? ' is-open' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open
        ? createPortal(
            <div
              ref={popupRef}
              className="ms__select-panel"
              role="listbox"
              aria-label={listLabel}
              style={style}
            >
              {options.map((option) => {
                const on = option.value === value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={on}
                    className={`ms__select-option${on ? ' is-on' : ''}`}
                    onClick={() => {
                      close();
                      onChange(option.value);
                    }}
                  >
                    <span>{option.label}</span>
                    {on ? <i className="pi pi-check" aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
